import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { getImageUrl, getPlaceholder } from '../api';
import { ProjectCard, ServiceCard } from '../components/Shared';
import ConstructionCanvas from '../components/ConstructionCanvas';

// Crane / accent color used everywhere below the slider
const CRANE = '#d39816';
// Canvas animation line color — gold, matches crane accent
const CANVAS_COLOR = '#d39816';

function CountUp({ target, suffix = '' }) {
  const [count, setCount] = useState(0); 
  const [active, setActive] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setActive(true); o.disconnect(); } }, { threshold: 0.5 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  useEffect(() => {
    if (!active) return;
    let s = 0; const step = target / (2000 / 16);
    const id = setInterval(() => { s = Math.min(s + step, target); setCount(Math.floor(s)); if (s >= target) clearInterval(id); }, 16);
    return () => clearInterval(id);
  }, [active, target]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

const SLIDES = [
  { src: '/slider/slide1.jpg', tag: 'Residential Excellence', title: 'Building Homes,\nCreating Legacies' },
  { src: '/slider/slide2.jpg', tag: 'Commercial Projects',    title: 'Iconic Structures\nfor Modern Business' },
  { src: '/slider/slide3.jpg', tag: 'Industrial Solutions',   title: 'Precision Engineering\nat Every Scale' },
  { src: '/slider/slide4.jpg', tag: 'Sustainable Design',     title: 'Eco-Conscious\nConstruction' },
];

// ── Hero Slider — NO canvas, pure photo + overlay ────────────
function HeroSlider({ settings }) {
  const [cur, setCur] = useState(0);
  const [key, setKey] = useState(0);
  const [prog, setProg] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null); const progTimer = useRef(null);
  const DUR = 5500;
  const goTo = useCallback(i => { setCur(i); setKey(k => k + 1); setProg(0); }, []);
  const next = useCallback(() => goTo((cur + 1) % 4), [cur, goTo]);
  const prev = useCallback(() => goTo((cur + 3) % 4), [cur, goTo]);
  useEffect(() => { if (paused) return; timer.current = setInterval(next, DUR); return () => clearInterval(timer.current); }, [paused, next]);
  useEffect(() => { if (paused) return; setProg(0); const t0 = Date.now(); progTimer.current = setInterval(() => setProg(Math.min((Date.now() - t0) / DUR * 100, 100)), 50); return () => clearInterval(progTimer.current); }, [cur, paused]);
  const siteName = settings.site_name || 'Bacha & Son';

  return (
    /* NO ConstructionCanvas here — pure photo slider */
    <section className="hero-slider-section"
      style={{ position: 'relative', height: '100vh', minHeight: 600, overflow: 'hidden' }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>

      {/* Photo slides */}
      {SLIDES.map((s, i) => (
        <div key={i} style={{ position: 'absolute', inset: 0, opacity: i === cur ? 1 : 0, transition: 'opacity 1.2s ease', zIndex: i === cur ? 2 : 1 }}>
          <picture>
            <source srcSet={s.src.replace(/\.jpg$/, '.webp')} type="image/webp" />
            <img src={s.src} alt="" className="hero-slide-img" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              fetchpriority={i === 0 ? 'high' : 'low'}
              onError={e => { e.target.src = 'https://placehold.co/1920x1080/141420/d39816?text=BachaAndSon'; }} />
          </picture>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg,rgba(20,20,30,0.82) 0%,rgba(20,20,30,0.42) 55%,transparent 100%)' }} />
        </div>
      ))}

      {/* Hero content */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center' }}>
        <div className="container">
          <div style={{ maxWidth: 630 }}>
            {/* Tag */}
            <div key={`tag-${key}`} className="hero-tag-wrap" style={{ marginBottom: 16, animation: 'fadeInUp 0.5s 0.05s ease both' }}>
              <span className="hero-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(211,152,22,0.15)', backdropFilter: 'blur(8px)', border: `1px solid rgba(211,152,22,0.35)`, color: CRANE, fontWeight: 700, fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase', padding: '6px 18px', borderRadius: 50 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: CRANE, display: 'inline-block' }} />
                {SLIDES[cur].tag}
              </span>
            </div>
            {/* Headline */}
            <h1 key={`h-${key}`} className="hero-title" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.6rem,5.5vw,4.8rem)', color: '#fff', lineHeight: 1.06, marginBottom: 26, whiteSpace: 'pre-line', textShadow: '0 4px 24px rgba(0,0,0,0.35)', animation: 'fadeInUp 0.55s 0.1s ease both' }}>
              {SLIDES[cur].title}
            </h1>
            {/* Sub */}
            <p key={`p-${key}`} className="hero-subtitle" style={{ color: 'rgba(255,255,255,0.62)', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: 34, maxWidth: 480, animation: 'fadeInUp 0.6s 0.18s ease both' }}>
              {settings.site_about?.substring(0, 150) || 'From concept to completion — delivering construction excellence that stands the test of time.'}
            </p>
            {/* CTA */}
            <div key={`cta-${key}`} className="hero-cta" style={{ display: 'flex', flexWrap: 'wrap', gap: 14, animation: 'fadeInUp 0.65s 0.25s ease both' }}>
              <Link to="/projects" className="btn-primary-pub"><i className="bi bi-grid-3x3-gap" /> Our Projects</Link>
              <Link to="/contact" className="btn-outline-light"><i className="bi bi-chat-text" /> Start a Project</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div style={{ position: 'absolute', bottom: 32, left: 0, right: 0, zIndex: 10 }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {SLIDES.map((_, i) => (
              <button key={i} onClick={() => goTo(i)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                <div style={{ height: 3, borderRadius: 2, transition: 'all 0.4s ease', width: i === cur ? 52 : 18,
                  background: i === cur ? `linear-gradient(90deg,${CRANE} ${prog}%,rgba(255,255,255,0.2) ${prog}%)` : 'rgba(255,255,255,0.25)' }} />
              </button>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              {[{ fn: prev, icon: 'bi-chevron-left' }, { fn: next, icon: 'bi-chevron-right' }].map(({ fn, icon }, i) => (
                <button key={i} onClick={fn} style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.22)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, transition: 'all 0.25s' }}
                  onMouseEnter={e => e.currentTarget.style.background = `rgba(211,152,22,0.45)`}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
                  <i className={`bi ${icon}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Reusable section wrapper helpers ─────────────────────────
const CW = { position: 'absolute', inset: 0, zIndex: 0 };  // canvas wrapper
const CZ = { position: 'relative', zIndex: 2 };             // content above canvas

export default function HomePage() {
  const [projects, setProjects]         = useState([]);
  const [services, setServices]         = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings]         = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/projects?limit=6&published=1').then(r => setProjects(r.data.data?.projects || [])).catch(() => {});
    api.get('/services?featured=1&limit=6').then(r => setServices(r.data.data || [])).catch(() => {});
    api.get('/testimonials?featured=1&limit=4').then(r => setTestimonials(r.data.data || [])).catch(() => {});
    api.get('/settings').then(r => setSettings(r.data.data || {})).catch(() => {});
  }, []);

  const stats = [
    { v: parseInt(settings.site_years_experience) || 25,   l: 'Years of Excellence', s: '+', icon: 'bi-award-fill' },
    { v: parseInt(settings.site_projects_completed) || 500, l: 'Projects Delivered',  s: '+', icon: 'bi-building-fill' },
    { v: parseInt(settings.site_happy_clients) || 350,     l: 'Satisfied Clients',   s: '+', icon: 'bi-people-fill' },
    { v: parseInt(settings.site_team_members) || 120,      l: 'Expert Team',         s: '+', icon: 'bi-person-gear' },
  ];

  const officeAddress = settings.site_address || '123 Construction Boulevard, Suite 500, Metro City, MC 10001';

  return (
    <>
      {/* ── 1. HERO SLIDER — dark, NO canvas ─────────────────── */}
      <HeroSlider settings={settings} />

      {/* ── 2. STATS — light warm bg + construction canvas ─────── */}
      {/* Canvas starts HERE, first section below slider */}
      <section style={{ position: 'relative', background: 'var(--site-bg-3)', padding: '80px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="blueprint" color={CANVAS_COLOR} opacity={0.45} /></div>
        <div className="container" style={CZ}>
          <div className="row g-4">
            {stats.map((s, i) => (
              <div key={i} className="col-6 col-lg-3" style={{ animation: `fadeInUp 0.55s ${0.1 * i}s ease both` }}>
                <div style={{ background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(10px)', border: '1px solid rgba(211,152,22,0.15)', borderRadius: 'var(--radius-xl)', padding: '32px 20px', textAlign: 'center', boxShadow: 'var(--shadow-sm)', transition: 'var(--transition)' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.15)'; }}>
                  <div style={{ width: 50, height: 50, borderRadius: '50%', background: 'rgba(211,152,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                    <i className={`bi ${s.icon}`} style={{ fontSize: 22, color: CRANE }} />
                  </div>
                  <div className="stat-number-lg" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.9rem,3.5vw,2.7rem)', fontWeight: 700, color: CRANE, lineHeight: 1, marginBottom: 8 }}>
                    <CountUp target={s.v} suffix={s.s} />
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{s.l}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. FEATURED PROJECTS — white bg + structure beams ──── */}
      <section style={{ position: 'relative', background: 'var(--site-bg-2)', padding: '26px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="structure" color={CANVAS_COLOR} opacity={0.35} /></div>
        <div className="container" style={CZ}>
          <div className="row align-items-end mb-5">
            <div className="col-lg-7">
              <span className="section-label">Our Portfolio</span>
              <h2 className="section-title">Featured <span>Projects</span></h2>
              <span className="divider-accent" />
              <p className="section-subtitle mb-0">Landmark projects spanning residential, commercial, and industrial sectors — each one a testament to craft.</p>
            </div>
            <div className="col-lg-5 text-lg-end mt-3 mt-lg-0">
              <Link to="/projects" className="btn-outline-pub">View All Projects <i className="bi bi-arrow-right ms-1" /></Link>
            </div>
          </div>
          {projects.length > 0 ? (
            <div className="row g-4">
              {projects.map((p, i) => (
                <div key={p.id} className="col-md-6 col-lg-4" style={{ animation: `fadeInUp 0.55s ${0.07 * i}s ease both` }}>
                  <ProjectCard project={p} onClick={() => navigate(`/projects/${p.slug}`)} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-building" style={{ fontSize: 52, display: 'block', marginBottom: 16, color: 'rgba(211,152,22,0.3)' }} />
              <p style={{ color: 'var(--text-muted)' }}>Projects will appear here once added.</p>
            </div>
          )}
        </div>
      </section>

      {/* ── 4. SERVICES — warm cream bg + crane animation ───────── */}
      <section style={{ position: 'relative', background: 'var(--site-bg-3)', padding: '26px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="crane" color={CANVAS_COLOR} opacity={0.45} /></div>
        <div className="container" style={CZ}>
          <div className="row align-items-end mb-5">
            <div className="col-lg-7">
              <span className="section-label">What We Offer</span>
              <h2 className="section-title">Our <span>Services</span></h2>
              <span className="divider-accent" />
              <p className="section-subtitle mb-0">Comprehensive construction solutions delivered with uncompromising craftsmanship.</p>
            </div>
            <div className="col-lg-5 text-lg-end mt-3 mt-lg-0">
              <Link to="/services" className="btn-outline-pub">All Services <i className="bi bi-arrow-right ms-1" /></Link>
            </div>
          </div>
          <div className="row g-4">
            {services.map((s, i) => (
              <div key={s.id} className="col-md-6 col-lg-4" style={{ animation: `fadeInUp 0.55s ${0.07 * i}s ease both` }}>
                <ServiceCard service={s} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. WHY US — white bg + full canvas ──────────────────── */}
      <section style={{ position: 'relative', background: 'var(--site-bg-2)', padding: '26px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="full" color={CANVAS_COLOR} opacity={0.35} /></div>
        <div className="container" style={CZ}>
          <div className="row align-items-center g-5">
            <div className="col-lg-5" style={{ animation: 'slideInL 0.65s ease both' }}>
              <span className="section-label">Why Bacha & Son</span>
              <h2 className="section-title">Building Trust <span>Since 1999</span></h2>
              <span className="divider-accent" />
              <p className="section-subtitle" style={{ marginBottom: 32 }}>{settings.site_about || '25+ years of experience. Cutting-edge techniques. Every project, every time.'}</p>
              <Link to="/about" className="btn-outline-pub">Our Story <i className="bi bi-arrow-right ms-1" /></Link>
            </div>
            <div className="col-lg-7">
              <div className="row g-3">
                {[
                  { icon: 'bi-award-fill',       t: 'Award-Winning Quality', d: 'Recognised by industry bodies for outstanding construction standards.' },
                  { icon: 'bi-clock-history',     t: 'On-Time Delivery',     d: '98% of projects delivered on schedule without compromising quality.' },
                  { icon: 'bi-shield-fill-check', t: 'Safety First',         d: 'Zero-accident record through rigorous safety protocols.' },
                  { icon: 'bi-tree-fill',         t: 'Sustainable Building', d: 'LEED-certified processes minimising environmental impact.' },
                ].map((f, i) => (
                  <div key={i} className="col-sm-6" style={{ animation: `fadeInUp 0.55s ${0.1 * i}s ease both` }}>
                    <div style={{ background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(6px)', border: '1px solid rgba(211,152,22,0.12)', borderRadius: 'var(--radius-lg)', padding: '24px 20px', height: '100%', transition: 'var(--transition)', boxShadow: 'var(--shadow-sm)' }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.3)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.12)'; }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(211,152,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className={`bi ${f.icon}`} style={{ fontSize: 20, color: CRANE }} />
                        </div>
                        <h6 style={{ color: 'var(--text)', marginBottom: 0, fontFamily: 'var(--font-display)', fontSize: '0.95rem' }}>{f.t}</h6>
                      </div>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.75, margin: 0 }}>{f.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6. TESTIMONIALS — warm cream + light canvas ─────────── */}
      {testimonials.length > 0 && (
        <section style={{ position: 'relative', background: 'var(--site-bg-3)', padding: '26px 0', overflow: 'hidden' }}>
          <div style={CW}><ConstructionCanvas variant="light" color={CANVAS_COLOR} opacity={0.35} /></div>
          <div className="container" style={CZ}>
            <div className="text-center mb-5">
              <span className="section-label">Client Voices</span>
              <h2 className="section-title">What Our <span>Clients Say</span></h2>
              <span className="divider-accent center" />
            </div>
            <div className="row g-4">
              {testimonials.map((t, i) => (
                <div key={t.id} className="col-md-6 col-lg-3" style={{ animation: `fadeInUp 0.55s ${0.1 * i}s ease both` }}>
                  <div style={{ background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)', border: '1px solid rgba(211,152,22,0.1)', borderRadius: 'var(--radius-lg)', padding: 28, height: '100%', transition: 'var(--transition)', boxShadow: 'var(--shadow-sm)' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.25)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.borderColor = 'rgba(211,152,22,0.1)'; }}>
                    <div style={{ display: 'flex', marginBottom: 12 }}>
                      {[...Array(t.rating || 5)].map((_, j) => <i key={j} className="bi bi-star-fill me-1" style={{ color: CRANE, fontSize: 12 }} />)}
                    </div>
                    <p style={{ color: 'var(--text-2)', fontSize: '0.9rem', lineHeight: 1.8, fontStyle: 'italic', marginBottom: 18 }}>"{t.message}"</p>
                    <div style={{ borderTop: '1px solid rgba(211,152,22,0.1)', paddingTop: 14 }}>
                      <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: '0.875rem' }}>{t.client_name}</div>
                      <div style={{ color: CRANE, fontSize: '0.8rem', marginTop: 2 }}>{t.client_title}{t.client_company ? `, ${t.client_company}` : ''}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 7. CTA — dark footer-style + minimal canvas ──────────── */}
      <section style={{ position: 'relative', background: 'var(--text)', paddingTop: 40, paddingBottom: 20, overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="minimal" color={CANVAS_COLOR} opacity={0.3} /></div>
        <div className="container" style={CZ}>
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <h2 className="home-cta-title" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem,3.5vw,2.8rem)', color: '#fff', marginBottom: 12 }}>
                Ready to <span style={{ color: CRANE, fontStyle: 'italic' }}>Build</span> Something Extraordinary?
              </h2>
              <p className="home-cta-subtitle" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.05rem', margin: 0 }}>Our expert team is ready to turn your vision into a landmark.</p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link to="/contact" className="btn-primary-pub"><i className="bi bi-chat-text" /> Start Your Project</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. LOCATION MAP — office location, directly above footer ── */}
      <section style={{ position: 'relative', background: 'var(--site-bg-2)', padding: '26px 0', overflow: 'hidden' }}>
        <div style={CW}><ConstructionCanvas variant="blueprint" color={CANVAS_COLOR} opacity={0.45} /></div>
        <div className="container" style={CZ}>
          <div className="text-center mb-4">
            <span className="section-label">Find Us</span>
            <h2 className="section-title">Visit Our <span>Office</span></h2>
            <span className="divider-accent center" />
          </div>
          <div className="home-map-frame" style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow)', border: '1px solid rgba(211,152,22,0.15)', height: 380 }}>
            <iframe
              title="Office Location"
              src={`https://www.google.com/maps?q=${encodeURIComponent(officeAddress)}&output=embed`}
              width="100%" height="100%" style={{ border: 0, display: 'block' }} loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="text-center mt-4">
            <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', margin: 0 }}>
              <i className="bi bi-geo-alt-fill me-2" style={{ color: CRANE }} />{officeAddress}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
